// Dispatch store manages the live responder chain state
// In production this is driven by Firestore + WebSocket
// Here we use a shared in-memory event emitter pattern

const listeners = new Set();
const dispatchState = new Map(); // incidentId -> dispatchChain[]

export function subscribeToDispatch(incidentId, cb) {
  const key = `${incidentId}:${Math.random()}`;
  const entry = { key, incidentId, cb };
  listeners.add(entry);
  return () => listeners.delete(entry);
}

function emit(incidentId, chain) {
  for (const l of listeners) {
    if (l.incidentId === incidentId) l.cb([...chain]);
  }
}

export function getDispatchChain(incidentId) {
  return dispatchState.get(incidentId) || [];
}

export function initDispatch(incidentId, chain) {
  if (!dispatchState.has(incidentId)) {
    dispatchState.set(incidentId, [...chain]);
  }
}

// Responder action: accept or decline
export function respondToDispatch(incidentId, responderId, action, reason = '') {
  const chain = dispatchState.get(incidentId) || [];
  const idx = chain.findIndex((d) => d.responderId === responderId);
  if (idx === -1) return;

  const now = Date.now();

  if (action === 'accept') {
    chain[idx] = { ...chain[idx], status: 'accepted', respondedAt: now };
    // All standby responders remain on standby (mentally prepared)
    // but don't need to do anything
  } else if (action === 'decline') {
    chain[idx] = { ...chain[idx], status: 'declined', respondedAt: now, reason };
    // Promote next standby responder to notified
    const nextStandby = chain.findIndex((d, i) => i > idx && d.status === 'standby');
    if (nextStandby !== -1) {
      chain[nextStandby] = { ...chain[nextStandby], status: 'notified', notifiedAt: now, timeoutAt: now + 30000 };
    }
  }

  dispatchState.set(incidentId, chain);
  emit(incidentId, chain);
  return chain;
}

// Auto-escalate on timeout (30s)
export function startTimeoutWatcher(incidentId) {
  const check = () => {
    const chain = dispatchState.get(incidentId);
    if (!chain) return;

    const now = Date.now();
    let changed = false;

    chain.forEach((d, idx) => {
      if (d.status === 'notified' && d.timeoutAt && now > d.timeoutAt) {
        chain[idx] = { ...d, status: 'declined', respondedAt: now, reason: 'No response — auto-escalated' };
        // Promote next standby
        const next = chain.findIndex((x, i) => i > idx && x.status === 'standby');
        if (next !== -1) {
          chain[next] = { ...chain[next], status: 'notified', notifiedAt: now, timeoutAt: now + 30000 };
        }
        changed = true;
      }
    });

    if (changed) {
      dispatchState.set(incidentId, chain);
      emit(incidentId, chain);
    }
  };

  const interval = setInterval(check, 1000);
  return () => clearInterval(interval);
}
